/**
 * Flota — warstwa wizualna analityki, tras i integracji tras (`fleet.web.anx.*`, `fleet.web.rtx.*`, `fleet.web.inx.*`),
 * doklejona w `extra.ts`. `fleetAnalyticsUiEn` jest typowany względem `fleetAnalyticsUiPl` — brak klucza = błąd kompilacji.
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const fleetAnalyticsUiPl = {
  anx: {
    header: { period: 'Okres', today: 'Stan na dziś', data: 'Dane', approvedOnly: 'tylko zaakceptowane', withUnapproved: 'z niezaakceptowanymi' },
    filters: { on: 'włączone', off: 'wyłączone' },
    heat: {
      title: 'Dzienny przebieg floty',
      description: 'Każdy kwadrat to jeden dzień — im ciemniejszy, tym więcej kilometrów przejechała cała flota.',
      noData: 'brak danych',
      aria: 'Mapa cieplna przebiegu: razem {total}, dni z przebiegiem: {days}',
      summary: 'Dni z przebiegiem: {days} · maksimum w dniu: {max}',
      wd: { mon: 'Pn', tue: 'Wt', wed: 'Śr', thu: 'Cz', fri: 'Pt', sat: 'So', sun: 'Nd' },
    },
    mileage: {
      documentedShare: '{pct} przebiegu udokumentowane kartami',
      docBar: 'Udokumentowanie',
      docBarAria: 'Karty drogowe: {logbook}, bez karty: {undocumented}',
    },
    costs: {
      avgPerVehicle: 'Średnio na pojazd',
      vehiclesCount: 'pojazdów w zestawieniu: {count}',
      structure: 'Struktura kosztów',
      structureHint: 'Paliwo, leasing i pozostałe koszty w wybranym okresie; lista poniżej pokazuje, które kategorie weszły do wyniku.',
      structureAria: 'Struktura kosztów, razem {total}',
      share: 'Udział',
    },
    fuel: {
      median: 'Mediana floty',
      medianHint: 'tylko pojazdy z pewnym wynikiem',
      medianLabel: 'mediana {value}',
      rankHint: 'Pojazdy od najniższego zużycia. Kreska = mediana floty. Wyniki niekompletne nie biorą udziału w rankingu — zobacz powody po najechaniu.',
      vsMedian: '{pct}% vs mediana',
      reasonsCount: 'powodów: {count}',
      incompleteHint: 'za mało danych o pełnych bakach',
    },
    vehicles: {
      median: 'Mediana koszt/km',
      medianHint: 'pojazdów z wynikiem: {count}',
      lowest: 'Najniższy koszt/km',
      highest: 'Najwyższy koszt/km',
      rankHint: 'Ranking pojazdów od najtańszego w przeliczeniu na kilometr. Kreska = mediana grupy. Dotyczy pojazdów, nie kierowców.',
      medianLabel: 'mediana {value}',
      indicative: 'orientacyjnie (brak stanu początkowego)',
    },
    leases: {
      count: 'Aktywne leasingi',
      ok: 'W normie',
      attention: 'Wymagają uwagi',
      exceededHint: 'przekroczone: {count}',
      forecast: 'Prognozowane przekroczenie',
      forecastHint: 'umów z prognozą ponad limit: {count}',
    },
    trend: {
      last: 'Ostatni miesiąc',
      change: 'Zmiana m/m',
      changeHint: 'względem poprzedniego miesiąca z danymi',
      avg: 'Średnia miesięczna',
      avgHint: 'z miesięcy z danymi: {count}',
      peak: 'Najwyższy miesiąc',
    },
  },
  rtx: {
    planShort: 'plan',
    actualShort: 'wykonanie',
    col: { planVsActual: 'Plan vs wykonanie' },
    compliance: {
      title: 'Zgodność planu z wykonaniem',
      hint: 'Trasy w tolerancji ±{tolerance}% wśród tras porównanych z kartą drogową',
      aria: 'Zgodność planu: {pct}% — {ok} z {compared} porównanych tras mieści się w tolerancji',
      distribution: 'Rozkład wyników porównania tras',
      diff: 'Różnica km (wykonanie − plan)',
      diffHint: 'tylko trasy porównane z kartą drogową',
    },
    detail: {
      barLegend: 'Wypełnienie = km z karty drogowej, kreska = plan trasy.',
      stopsHint: 'Kolejność punktów dostaw według planu trasy (planer Routimo albo plik CSV).',
      noTime: 'bez godziny',
      stopN: 'Punkt {n}',
    },
  },
  inx: {
    header: { source: 'Źródło', csv: 'plik CSV' },
    health: {
      title: 'Stan integracji tras',
      description: 'Źródło danych i wynik ostatniego importu.',
      notConnected: 'System tras nie jest podłączony — nic nie pobiera się automatycznie. Trasy trafiają do Floty wyłącznie z wgranego pliku CSV.',
      none: 'Nie wykonano jeszcze importu',
      noneHint: 'Wgraj plik CSV na zakładce Import — najpierw zobaczysz podgląd bez zapisu.',
      last: 'Ostatni import',
      created: 'Nowe trasy',
      updated: 'Zaktualizowane',
      rejected: 'Odrzucone wiersze',
      unmapped: 'Niezmapowane trasy',
      report: 'Otwórz raport',
    },
    stepper: { aria: 'Kroki importu', step: 'Krok {n}', file: 'Wybierz plik', preview: 'Podgląd', import: 'Import', report: 'Raport', done: 'ukończony', current: 'bieżący krok' },
    report: { byCode: 'Błędy według rodzaju' },
  },
};

export const fleetAnalyticsUiEn: Shape<typeof fleetAnalyticsUiPl> = {
  anx: {
    header: { period: 'Period', today: 'As of today', data: 'Data', approvedOnly: 'approved only', withUnapproved: 'incl. unapproved' },
    filters: { on: 'on', off: 'off' },
    heat: {
      title: 'Daily fleet mileage',
      description: 'Each square is one day — the darker it is, the more kilometres the whole fleet drove.',
      noData: 'no data',
      aria: 'Mileage heatmap: total {total}, days with mileage: {days}',
      summary: 'Days with mileage: {days} · daily maximum: {max}',
      wd: { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' },
    },
    mileage: {
      documentedShare: '{pct} of mileage documented by logbooks',
      docBar: 'Documented',
      docBarAria: 'Logbooks: {logbook}, without logbook: {undocumented}',
    },
    costs: {
      avgPerVehicle: 'Average per vehicle',
      vehiclesCount: 'vehicles in the report: {count}',
      structure: 'Cost structure',
      structureHint: 'Fuel, lease and other costs in the selected period; the list below shows which categories were included.',
      structureAria: 'Cost structure, total {total}',
      share: 'Share',
    },
    fuel: {
      median: 'Fleet median',
      medianHint: 'vehicles with a reliable result only',
      medianLabel: 'median {value}',
      rankHint: 'Vehicles from the lowest consumption. Line = fleet median. Incomplete results are excluded from the ranking — hover to see the reasons.',
      vsMedian: '{pct}% vs median',
      reasonsCount: 'reasons: {count}',
      incompleteHint: 'not enough full-tank data',
    },
    vehicles: {
      median: 'Median cost/km',
      medianHint: 'vehicles with a result: {count}',
      lowest: 'Lowest cost/km',
      highest: 'Highest cost/km',
      rankHint: 'Vehicles ranked from the cheapest per kilometre. Line = group median. Applies to vehicles, not drivers.',
      medianLabel: 'median {value}',
      indicative: 'indicative (no baseline reading)',
    },
    leases: {
      count: 'Active leases',
      ok: 'Within limits',
      attention: 'Need attention',
      exceededHint: 'exceeded: {count}',
      forecast: 'Forecast overage',
      forecastHint: 'contracts forecast above the limit: {count}',
    },
    trend: {
      last: 'Latest month',
      change: 'Change m/m',
      changeHint: 'vs the previous month with data',
      avg: 'Monthly average',
      avgHint: 'from months with data: {count}',
      peak: 'Highest month',
    },
  },
  rtx: {
    planShort: 'plan',
    actualShort: 'actual',
    col: { planVsActual: 'Plan vs actual' },
    compliance: {
      title: 'Plan compliance',
      hint: 'Routes within ±{tolerance}% among routes compared with a logbook',
      aria: 'Plan compliance: {pct}% — {ok} of {compared} compared routes are within tolerance',
      distribution: 'Route comparison breakdown',
      diff: 'Difference km (actual − plan)',
      diffHint: 'routes compared with a logbook only',
    },
    detail: {
      barLegend: 'Fill = km from the logbook, line = route plan.',
      stopsHint: 'Order of delivery stops as planned (Routimo planner or CSV file).',
      noTime: 'no time',
      stopN: 'Stop {n}',
    },
  },
  inx: {
    header: { source: 'Source', csv: 'CSV file' },
    health: {
      title: 'Route integration status',
      description: 'Data source and the result of the last import.',
      notConnected: 'The route system is not connected — nothing is fetched automatically. Routes reach Fleet only from an uploaded CSV file.',
      none: 'No import has been run yet',
      noneHint: 'Upload a CSV file on the Import tab — you will see a preview first, with nothing saved.',
      last: 'Last import',
      created: 'New routes',
      updated: 'Updated',
      rejected: 'Rejected rows',
      unmapped: 'Unmapped routes',
      report: 'Open report',
    },
    stepper: { aria: 'Import steps', step: 'Step {n}', file: 'Choose file', preview: 'Preview', import: 'Import', report: 'Report', done: 'completed', current: 'current step' },
    report: { byCode: 'Errors by type' },
  },
};
